/**
 * 플랫폼 최상위 Overview (`/platform`) — 랜딩에서 "저작·실행 플랫폼"을 고르면
 * 도착하는 첫 화면 (2026-08-11 최상위 랜딩 개편 → 2026-08-12 배치 개편).
 *
 * 메인: Workspaces/Simulations 진입 카드 + **이슈 게시판 보드 내장**(별도
 * 페이지 링크가 아니라 여기서 바로 등록·왕복 — 2026-08-12 사용자 결정).
 * 우측: 리사이저블 사이드 패널(공지·Recent Activity·Tutorial) — 픽커 시절
 * SideInfoPanel 의 복원. 메인 콘텐츠는 짧을 때 세로 중앙 정렬로 상단 쏠림을
 * 막는다.
 */

import React, { useEffect, useState, lazy, Suspense } from 'react';
import { useAsyncScope } from '@/shared/async/useAsyncScope';
import { useNavigate } from 'react-router-dom';
import { Box, Chip, IconButton, Paper, Stack, Typography } from '@mui/material';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AppLayout from '@/app/chrome/AppLayout';
import { rootNavItems } from '@/app/chrome/rootNav';
import HeaderUtilities from '@/shared/ui/HeaderUtilities';
import WidthResizeHandle from '@/shared/ui/WidthResizeHandle';
import AnnouncementsPanel from '@/features/announcements/AnnouncementsPanel';
import RecentActivityPanel from '@/features/home/RecentActivityPanel';
import AnnouncementBanner from '@/features/announcements/AnnouncementBanner';
import IssueBoard from '@/features/issues/IssueBoard';
import { useResizableWidth } from '@/shared/hooks/useResizableWidth';
import { useElementWidth } from '@/shared/hooks/useElementWidth';
import { SIDE_PANEL_WIDTH, sidePanelMaxWidth } from '@/features/home/sidePanelWidth';
import { listProjects } from '@/app/services/pm/projectManagerService';
import { useUiChromeStore } from '@/app/stores/uiChromeStore';
import { useAuthStore } from '@/app/stores/authStore';
import { useLogout } from '@/features/auth/useLogout';
import type { ProjectSummary } from '@/types/project';
import { LazyFallback } from '@/shared/ui/LazyFallback';
import { QUICKRUN_PROJECT_ID } from '@/app/quickRunProject';

// 튜토리얼 패널은 본문·예제 설치(도메인)를 끌어오므로 진입 청크 밖(트랙 A-4) — 홈이 뜬 뒤 받는다
const TutorialPanel = lazy(() => import('@/features/home/TutorialPanel'));

interface EntryCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onEnter: () => void;
}

const EntryCard: React.FC<EntryCardProps> = ({ icon, title, description, onEnter }) => (
  <Paper
    elevation={0}
    role="button"
    tabIndex={0}
    onClick={onEnter}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onEnter();
      }
    }}
    sx={{
      p: 3,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      cursor: 'pointer',
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      '&:hover, &:focus-visible': { borderColor: 'primary.main', boxShadow: 2 },
    }}
  >
    <Box
      sx={{
        width: 52,
        height: 52,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'action.hover',
        color: 'primary.main',
      }}
    >
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
    <ArrowForwardIcon color="action" />
  </Paper>
);

const PlatformHomePage: React.FC = () => {
  const navigate = useNavigate();
  const handleLogout = useLogout();
  const sidebarExpanded = useUiChromeStore((s) => s.sidebarExpanded);
  const toggleSidebar = useUiChromeStore((s) => s.toggleSidebar);
  const userId = useAuthStore((s) => s.user?.id);

  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsFailed, setProjectsFailed] = useState(false);

  // 우측 정보 패널 접힘 — 구 픽커의 셰브론 토글과 동일 문법, 브라우저 단위 기억.
  // 저장된 선택이 없으면 xl(1536px) 미만에서는 접힌 채 시작한다 — 1440 폭에서 패널과
  // 토글이 함께 사라져 튜토리얼이 설명하는 패널을 찾을 수 없었다(게시판 #20). 토글은
  // lg 부터 항상 보이고, 펼치면 폭 상한(남는 자리, sidePanelMaxWidth)이 메인을 지킨다.
  const [panelCollapsed, setPanelCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem('vsmr.platformSidePanel.collapsed');
      if (stored === '1') return true;
      if (stored === '0') return false;
    } catch {
      // 읽기 실패는 기본값으로
    }
    return typeof window !== 'undefined' && !window.matchMedia('(min-width: 1536px)').matches;
  });
  const setPanelCollapsedPersist = (next: boolean) => {
    setPanelCollapsed(next);
    try {
      localStorage.setItem('vsmr.platformSidePanel.collapsed', next ? '1' : '0');
    } catch {
      // 저장 실패는 무해 — 이번 세션 동안만 유지
    }
  };

  // 우측 정보 패널 — 픽커 시절 SideInfoPanel 과 같은 문법의 드래그 리사이즈.
  // minWidth 미만으로 끌면 완전 접힘(셰브론으로 재펼침). 폭 상한은 본문 행의 남는 자리에서
  // 온다(platformHomeSidePanel) — 훅이 상한을 쥐어야 픽셀 상태와 렌더 폭이 같다.
  const [bodyRowRef, bodyRowWidth] = useElementWidth<HTMLDivElement>();
  const { width: panelWidth, dragging, handleProps } = useResizableWidth({
    storageKey: 'vsmr.platformSidePanel.width',
    defaultWidth: SIDE_PANEL_WIDTH.default,
    minWidth: SIDE_PANEL_WIDTH.min,
    maxWidth: sidePanelMaxWidth(bodyRowWidth),
    handleEdge: 'left',
    onCollapse: () => setPanelCollapsedPersist(true),
  });

  const feedScope = useAsyncScope(userId ?? null);
  useEffect(() => {
    if (!userId) return;
    const owns = feedScope.claim();
    (async () => {
      try {
        const list = await listProjects(userId);
        // 퀵런 전용 프로젝트는 피드에서 제외 — 픽커 시절 피드와 동일 규칙.
        // 퀵런이 잦으면 최근-프로젝트 슬롯 하나를 상시 점유해 일반 활동을 밀어낸다.
        if (owns()) setProjects(list.filter((proj) => proj.id !== QUICKRUN_PROJECT_ID));
      } catch {
        // 피드는 부가 정보 — 실패해도 Overview 자체는 성립하되, 빈 계정으로
        // 위장하지 않도록 실패 상태를 패널에 전달한다.
        if (owns()) setProjectsFailed(true);
      } finally {
        if (owns()) setProjectsLoading(false);
      }
    })();
  }, [userId, feedScope]);

  return (
    <AppLayout
      sidebarExpanded={sidebarExpanded}
      onSidebarToggle={toggleSidebar}
      sidebarItems={rootNavItems('overview', navigate)}
      activeSidebarItemId="overview"
      onLogout={handleLogout}
      onAccountSettings={() => navigate('/settings')}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* 전폭 헤더 — 타이틀 좌 / [날짜·알림·메일] 우상단 (구 픽커와 동일 배열).
            헤더가 패널 위까지 걸치므로 패널 콘텐츠는 자연히 그 아래에서 시작한다. */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{ px: 3, pt: 3, pb: 2 }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5">저작·실행 플랫폼</Typography>
            <Typography variant="body2" color="text.secondary">
              Modeling &amp; Simulation — 모델을 만들고, 돌리고, 해석합니다
            </Typography>
          </Box>
          <HeaderUtilities />
        </Stack>

        {/* 공지 — 안 읽은 것이 있을 때만 한 줄. 없으면 아무 자리도 차지하지 않는다 */}
        <AnnouncementBanner />

        <Box ref={bodyRowRef} sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
          {/* 메인 — 진입 카드 + 이슈 게시판 */}
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ width: '100%', maxWidth: 960, mx: 'auto', px: 3, pb: 3 }}>
                  {renderMainContent()}
                </Box>
              </Box>
              {/* 정보 패널 접기/펼치기 — 구 픽커처럼 메인 우측 끝(패널 경계 옆) */}
              <IconButton
                onClick={() => setPanelCollapsedPersist(!panelCollapsed)}
                aria-label={panelCollapsed ? '정보 패널 펼치기' : '정보 패널 접기'}
                sx={{
                  display: { xs: 'none', lg: 'inline-flex' },
                  width: 36,
                  height: 36,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  mr: 1.5,
                  flexShrink: 0,
                }}
              >
                {panelCollapsed ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              </IconButton>
            </Box>
          </Box>

          {/* 우측 정보 패널 — 공지 · Recent Activity · Tutorial (리사이저블·접힘 가능) */}
          <Box
            sx={{
              position: 'relative',
              width: panelWidth,
              flexShrink: 0,
              // lg 부터 존재(기본은 접힘, 위 useState). 폭 상한은 훅이 쥔다(sidePanelMaxWidth) —
              // CSS 로 따로 캡하면 저장 폭과 렌더 폭이 어긋난다.
              display: { xs: 'none', lg: panelCollapsed ? 'none' : 'flex' },
              flexDirection: 'column',
              borderLeft: 1,
              borderColor: 'divider',
              // 스크롤은 안쪽 스택에만 — 바깥이 스크롤 컨테이너면 절대 위치
              // 리사이즈 핸들이 콘텐츠와 함께 밀려 사라진다 (리뷰 반영)
              overflow: 'hidden',
              transition: dragging ? 'none' : 'width 0.15s ease',
            }}
          >
            <WidthResizeHandle edge="left" active={dragging} {...handleProps} />
            <Stack spacing={2} sx={{ p: 2, pt: 0.5, flex: 1, minHeight: 0, overflow: 'auto' }}>
              <AnnouncementsPanel />
              <RecentActivityPanel
                userId={userId}
                projects={projects}
                loading={projectsLoading}
                loadFailed={projectsFailed}
              />
              <Suspense fallback={<LazyFallback height={240} />}><TutorialPanel /></Suspense>
            </Stack>
          </Box>
        </Box>
      </Box>
    </AppLayout>
  );

  function renderMainContent() {
    return (
      <>
        {/* 진입 카드 — 플랫폼 2축 */}
            <Box
              sx={{
                display: 'grid',
                // 뷰포트가 아니라 실제 컨테이너 폭 기준 — 사이드바+패널이
                // 최대일 때 md 강제 2열이 카드를 넘치게 한다 (리뷰 반영)
                // min(280px, 100%) — 280px 최소가 컨테이너보다 크면(초협폭)
                // 컨테이너 폭까지 줄어들어 오버플로우가 없다 (리뷰 반영)
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
                gap: 2,
                mb: 4,
              }}
            >
              <EntryCard
                icon={<WorkspacesIcon />}
                title="Workspaces"
                description="프로젝트·모델 저작 — 워크스페이스로 조직"
                onEnter={() => navigate('/projects')}
              />
              <EntryCard
                icon={<ListAltIcon />}
                title="Simulations"
                description="실행 중·완료 실행 관리 — 이어보기와 결과"
                onEnter={() => navigate('/simulations')}
              />
            </Box>

        {/* 이슈 게시판 — 베타 피드백 창구를 Overview 메인에 내장 */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <BugReportOutlinedIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={600}>
            이슈 게시판
          </Typography>
          <Chip label="베타" size="small" color="primary" variant="outlined" sx={{ height: 20 }} />
        </Stack>
        <IssueBoard />
      </>
    );
  }
};

export default PlatformHomePage;
